import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { isMenopauseModeActive } from '@/logic/menopause';
import OnboardingHeader from '@/components/shared/OnboardingHeader';
import { t } from '@/locales/strings';
import { supabase } from '@/lib/supabase';
import { playConfirmDing, vibrateShort } from '@/utils/sound';

const BODY_OPTIONS = ['relaxed', 'tense', 'numb', 'aware', 'sensitive', 'disconnected'];

const questionStyle = {
  fontFamily: 'var(--font-title)', fontStyle: 'italic', fontSize: 22,
  fontWeight: 500,
  color: 'var(--dark-brown)', lineHeight: 1.25, marginBottom: 18,
  letterSpacing: '-0.01em',
  fontVariationSettings: '"opsz" 72',
};

const cardTextStyle = {
  flex: 1,
  fontFamily: 'var(--font-body)',
  fontSize: 16,
  fontWeight: 500,
  color: 'var(--dark-brown)',
  lineHeight: 1.4,
  letterSpacing: '-0.005em',
};

const checkMarkStyle = {
  width: 24, height: 24, borderRadius: 12,
  background: 'var(--primary)', color: '#fff',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 14, flexShrink: 0,
  animation: 'reflectionCheckPop 0.3s ease-out',
};

// Inline line-icons used in the option cards on the Body tab and the
// yes/no cards on the Intimate tab. Rose when selected, warm-brown
// when not — matches the rest of the design-system pass.
function ReflectionIcon({ name, selected }) {
  // currentColor lets the SVG inherit from the wrapper's CSS color.
  // The wrapper sets color to var(--primary) when selected (so the
  // icon themes per palette), warm-brown otherwise.
  const common = {
    width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
    style: { flexShrink: 0, color: selected ? 'var(--primary)' : 'var(--warm-brown)' },
  };
  const stroke = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'relaxed':
      // wave — soft relaxed line
      return (
        <svg {...common}>
          <path d="M3 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0" {...stroke} />
        </svg>
      );
    case 'tense':
      // coil / zig-zag
      return (
        <svg {...common}>
          <path d="M4 8l4-3 4 3 4-3 4 3M4 16l4-3 4 3 4-3 4 3" {...stroke} />
        </svg>
      );
    case 'numb':
      // dotted line — fill inherits from the svg's CSS color so the
      // dots follow the same active/inactive tint as the stroke.
      return (
        <svg {...common}>
          <circle cx="5" cy="12" r="1" fill="currentColor" />
          <circle cx="10" cy="12" r="1" fill="currentColor" />
          <circle cx="14" cy="12" r="1" fill="currentColor" />
          <circle cx="19" cy="12" r="1" fill="currentColor" />
        </svg>
      );
    case 'aware':
      // eye
      return (
        <svg {...common}>
          <path d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6S2 12 2 12z" {...stroke} />
          <circle cx="12" cy="12" r="2.5" {...stroke} />
        </svg>
      );
    case 'sensitive':
      // spark / sparkle
      return (
        <svg {...common}>
          <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6" {...stroke} />
        </svg>
      );
    case 'disconnected':
      // broken link
      return (
        <svg {...common}>
          <path d="M10 14l-2 2a3 3 0 0 1-4-4l2-2M14 10l2-2a3 3 0 0 1 4 4l-2 2M9 15l6-6" {...stroke} />
        </svg>
      );
    case 'yes':
      // checkmark in circle
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" {...stroke} />
          <path d="M8 12.5l2.5 2.5L16 9.5" {...stroke} />
        </svg>
      );
    case 'no':
      // x in circle
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" {...stroke} />
          <path d="M9 9l6 6M15 9l-6 6" {...stroke} />
        </svg>
      );
    default:
      // fallback — plain ring
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="7" {...stroke} />
        </svg>
      );
  }
}
const DESIRE_OPTIONS = ['none', '1x', '2x', '3x', '4plus'];
const LUBRICATION_OPTIONS = ['none', 'low', 'normal', 'high'];
const BOWEL_OPTIONS = ['none', 'normal', 'irregular', 'uncomfortable'];
const EXPERIENCE_OPTIONS = ['painful', 'uncomfortable', 'neutral', 'pleasant', 'very_pleasurable'];
const FLOW_OPTIONS = ['none', 'spotting', 'light', 'medium', 'heavy'];
const FLOOR_FEEL_OPTIONS = ['heavy', 'aching', 'quiet', 'strong', 'sensitive'];
const SYMPTOM_OPTIONS = ['cramps', 'headache', 'bloating', 'breast_tenderness', 'mood_low', 'mood_anxious', 'mood_calm', 'mood_joyful', 'energy_low', 'energy_high'];
// Menopause-specific symptoms surfaced as a second pill grid below
// the regular SYMPTOM_OPTIONS when the user has self-declared peri /
// meno / post in the stage tile. Items are an evidence-based subset
// of the Greene Climacteric Scale + GSM (genitourinary syndrome of
// menopause) cluster that PFMT directly impacts. We keep it short
// because phone screens cannot host 21 toggles, and because the
// items here are the ones our practice can actually move.
const MENOPAUSE_SYMPTOM_OPTIONS = [
  'hot_flushes',
  'night_sweats',
  'sleep_disturbance',
  'mood_shift',
  'vaginal_dryness',
  'urinary_urgency',
  'incontinence',
  'painful_sex',
  'joint_pain',
  'pelvic_heaviness',
];

const TAB_KEYS = ['body', 'cycle', 'signals', 'intimate'];
const SIGNAL_SUB_KEYS = ['desire', 'lubrication', 'bowel'];

// Inject keyframe animation once on first mount.
let styleInjected = false;
function injectStyles() {
  if (styleInjected) return;
  styleInjected = true;
  const sheet = document.createElement('style');
  sheet.textContent = `
    @keyframes reflectionFadeIn {
      from { opacity: 0; transform: scale(0.92); }
      to   { opacity: 1; transform: scale(1); }
    }
    @keyframes reflectionCheckPop {
      0%   { opacity: 0; transform: scale(0.5); }
      60%  { transform: scale(1.15); }
      100% { opacity: 1; transform: scale(1); }
    }
    .reflection-card:active {
      opacity: 0.7;
      transform: scale(0.98);
    }
    .reflection-card {
      transition: border-color 0.15s, background 0.15s, opacity 0.1s, transform 0.1s;
    }
    .reflection-pill:active {
      opacity: 0.7;
    }
    .reflection-save-btn:active {
      transform: scale(0.97);
      opacity: 0.85 !important;
    }
  `;
  document.head.appendChild(sheet);
}

export default function DailyReflection() {
  const navigate = useNavigate();
  const { lang, user, userProgress } = useApp();
  const menopauseModeOn = isMenopauseModeActive(userProgress?.menopause_stage);
  const [searchParams] = useSearchParams();

  // `targetDate` is the date the screen is editing. When a user taps a
  // past day in the Calendar, the URL becomes /reflection?date=YYYY-MM-DD
  // and all reads + writes switch to that date. Without the query param
  // the screen behaves exactly as before — bound to today.
  const todayISO = new Date().toISOString().slice(0, 10);
  const rawDate = searchParams.get('date');
  const targetDate = /^\d{4}-\d{2}-\d{2}$/.test(rawDate || '') ? rawDate : todayISO;
  const isPastDate = targetDate !== todayISO;

  useEffect(() => { injectStyles(); }, []);

  const [activeTab, setActiveTab] = useState(0);
  const [bodyFeelings, setBodyFeelings] = useState(new Set());
  const [signalSub, setSignalSub] = useState(0);
  const [sexualDesire, setSexualDesire] = useState(null);
  const [lubrication, setLubrication] = useState(null);
  const [bowel, setBowel] = useState(null);
  const [flow, setFlow] = useState(null);
  const [floorFeel, setFloorFeel] = useState(null);
  const [symptoms, setSymptoms] = useState(new Set());
  // Menopause-specific symptoms tracked alongside `symptoms` as a
  // separate Set so future analytics can split phase-of-life trends
  // (regular cycles vs. peri/post) without back-classifying entries.
  // Persisted as `signals.menopause_symptoms` array in diary_entries.
  const [menopauseSymptoms, setMenopauseSymptoms] = useState(new Set());
  const [note, setNote] = useState('');
  const [hadActivity, setHadActivity] = useState(null);
  const [experience, setExperience] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Prefill from the targetDate's existing diary_entries row (if any).
  // Re-runs when the user navigates between different past dates.
  useEffect(() => {
    if (!user?.id || !supabase) { setLoading(false); return; }
    let cancelled = false;
    // Reset all selections when switching dates so we don't carry over
    // state from a previous date.
    setBodyFeelings(new Set());
    setSexualDesire(null);
    setLubrication(null);
    setBowel(null);
    setFlow(null);
    setFloorFeel(null);
    setSymptoms(new Set());
    setMenopauseSymptoms(new Set());
    setNote('');
    setHadActivity(null);
    setExperience(null);
    setLoading(true);
    supabase
      .from('diary_entries')
      .select('body_feeling, signals, intimate')
      .eq('user_id', user.id)
      .eq('entry_date', targetDate)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (!data) { setLoading(false); return; }
        try {
          const bf = JSON.parse(data.body_feeling || '[]');
          if (Array.isArray(bf) && bf.length) setBodyFeelings(new Set(bf));
        } catch {}
        if (data.signals) {
          if (data.signals.sexual_desire) setSexualDesire(data.signals.sexual_desire);
          if (data.signals.lubrication) setLubrication(data.signals.lubrication);
          if (data.signals.bowel) setBowel(data.signals.bowel);
          if (data.signals.flow) setFlow(data.signals.flow);
          if (data.signals.floor_feel) setFloorFeel(data.signals.floor_feel);
          if (Array.isArray(data.signals.symptoms)) setSymptoms(new Set(data.signals.symptoms));
          if (Array.isArray(data.signals.menopause_symptoms)) setMenopauseSymptoms(new Set(data.signals.menopause_symptoms));
          if (typeof data.signals.note === 'string') setNote(data.signals.note);
        }
        if (data.intimate) {
          if (data.intimate.had_activity) setHadActivity(data.intimate.had_activity);
          if (data.intimate.experience) setExperience(data.intimate.experience);
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, targetDate]);

  // Validation: require at least one selection across any tab.
  const hasData =
    bodyFeelings.size > 0 ||
    sexualDesire !== null ||
    lubrication !== null ||
    bowel !== null ||
    flow !== null ||
    floorFeel !== null ||
    symptoms.size > 0 ||
    menopauseSymptoms.size > 0 ||
    note.trim().length > 0 ||
    hadActivity !== null;

  function toggleBody(val) {
    setBodyFeelings((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveError(false);
    if (user?.id && supabase) {
      // Surface upsert failures so the user knows their reflection
      // didn't persist — the previous fire-and-forget catch silently
      // swallowed RLS / network errors and showed a fake "saved"
      // confirmation. Body feel is sensitive data; data loss here
      // breaks trust.
      try {
        const { error } = await supabase
          .from('diary_entries')
          .upsert(
            {
              user_id: user.id,
              entry_date: targetDate,
              body_feeling: JSON.stringify([...bodyFeelings]),
              signals: {
                sexual_desire: sexualDesire,
                lubrication,
                bowel,
                flow,
                floor_feel: floorFeel,
                symptoms: symptoms.size > 0 ? [...symptoms] : null,
                menopause_symptoms: menopauseSymptoms.size > 0 ? [...menopauseSymptoms] : null,
                note: note.trim().length > 0 ? note.trim() : null,
              },
              intimate: { had_activity: hadActivity, experience: hadActivity === 'yes' ? experience : null },
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id,entry_date' }
          );
        if (error) throw error;
      } catch {
        setSaving(false);
        setSaveError(true);
        return;
      }
    }
    setSaved(true);
    // Opt-in audio + (default-on) haptic confirmation. Both are
    // gated inside sound.js — calling them here unconditionally is
    // safe whether the user has either or both turned off.
    playConfirmDing();
    vibrateShort();
    // Past-date edits return to /progress (where the calendar lives and
    // the user was likely looking at their month). Today-edits return
    // to /home as before.
    const returnTo = isPastDate ? '/progress' : '/home';
    setTimeout(() => navigate(returnTo, { replace: true }), 1500);
  }

  // Header date string: full weekday + month + day, localised. For
  // past-date edits we reuse this in the `reflection_title_past`
  // template. For today it's the regular subtitle.
  const targetDateLabel = useMemo(() => (
    new Date(targetDate + 'T12:00:00').toLocaleDateString(
      lang === 'pt' ? 'pt-BR' : 'en-US',
      { weekday: 'long', month: 'long', day: 'numeric' },
    )
  ), [targetDate, lang]);

  return (
    <div style={{
      background: 'var(--bg)', minHeight: '100dvh', maxWidth: 520, margin: '0 auto',
      display: 'flex', flexDirection: 'column', position: 'relative',
    }}>
      {/* Shared header — back returns to /home, skip dismisses the
          reflection screen without persisting an entry. */}
      <OnboardingHeader
        onBack={() => navigate('/home', { replace: true })}
        onSkip={() => navigate('/home', { replace: true })}
      />

      {/* Title block */}
      <div className="motion-rise" style={{ padding: '8px 20px 0', textAlign: 'center', '--stagger-i': 0 }}>
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: 'var(--warm-brown)', marginBottom: 10,
        }}>
          {lang === 'pt' ? 'DIÁRIO' : 'DIARY'}
        </div>
        <h1 style={{
          fontFamily: 'var(--font-title)', fontStyle: 'italic', fontSize: 32,
          fontWeight: 500, color: 'var(--dark-brown)', margin: '0 0 6px',
          lineHeight: 1.15, letterSpacing: '-0.015em',
          fontVariationSettings: '"opsz" 96',
        }}>
          {isPastDate
            ? t('reflection_title_past', lang).replace('{date}', targetDateLabel)
            : t('reflection_title', lang)}
        </h1>
        {/* On today-edits, the sub-line is the localised date. For
            past-date edits the date is already in the title, so we
            surface an editing-indicator instead. */}
        <div style={{
          fontFamily: 'var(--font-ui)', fontSize: 12,
          color: 'var(--warm-brown)', textTransform: isPastDate ? 'none' : 'capitalize',
          letterSpacing: '0.02em',
        }}>
          {isPastDate ? t('reflection_past_subtitle', lang) : targetDateLabel}
        </div>
      </div>

      {/* Tabs — horizontal scroll on narrow screens so 4 pills fit
          without cramming. The scrollbar is hidden, but momentum scroll
          keeps every tab reachable on a 320px viewport. */}
      <div style={{
        display: 'flex', gap: 6, padding: '12px 20px 0',
        justifyContent: 'flex-start',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
      }}>
        {TAB_KEYS.map((key, idx) => {
          const isActive = activeTab === idx;
          return (
            <button
              type="button" key={key}
              role="tab"
              aria-selected={isActive}
              className="reflection-pill"
              onClick={() => setActiveTab(idx)}
              style={{
                padding: '9px 14px', borderRadius: 999,
                background: isActive ? 'var(--primary)' : 'var(--surface)',
                color: isActive ? '#fff' : 'var(--primary)',
                border: `1px solid ${isActive ? 'var(--primary)' : 'color-mix(in srgb, var(--primary) 30%, transparent)'}`,
                fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.06em',
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                boxShadow: isActive ? '0 8px 22px -10px rgba(168,0,63,0.38)' : 'none',
                transition: 'background 220ms ease, color 220ms ease, box-shadow 220ms ease',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t(`reflection_tab_${key}`, lang)}
            </button>
          );
        })}
      </div>

      {/* Tab content — scrollable area */}
      <div style={{
        flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        padding: '20px 20px calc(80px + max(16px, env(safe-area-inset-bottom)))',
        opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s',
      }}>
        {activeTab === 0 && <BodyTab bodyFeelings={bodyFeelings} toggleBody={toggleBody} lang={lang} />}
        {activeTab === 1 && (
          <CycleTab
            flow={flow} setFlow={setFlow}
            floorFeel={floorFeel} setFloorFeel={setFloorFeel}
            symptoms={symptoms} setSymptoms={setSymptoms}
            menopauseSymptoms={menopauseSymptoms} setMenopauseSymptoms={setMenopauseSymptoms}
            menopauseModeOn={menopauseModeOn}
            note={note} setNote={setNote}
            lang={lang}
          />
        )}
        {activeTab === 2 && (
          <SignalsTab
            signalSub={signalSub} setSignalSub={setSignalSub}
            sexualDesire={sexualDesire} setSexualDesire={setSexualDesire}
            lubrication={lubrication} setLubrication={setLubrication}
            bowel={bowel} setBowel={setBowel}
            lang={lang}
          />
        )}
        {activeTab === 3 && (
          <IntimateTab
            hadActivity={hadActivity} setHadActivity={setHadActivity}
            experience={experience} setExperience={setExperience}
            lang={lang}
          />
        )}
      </div>

      {/* Save button — fixed at bottom, overlays content with gradient */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        maxWidth: 520, margin: '0 auto',
        padding: '24px 20px',
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        background: 'linear-gradient(rgba(253,240,244,0), var(--surface-alt) 40%)',
        pointerEvents: 'none',
      }}>
        <div style={{ pointerEvents: 'auto' }}>
          {saved ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              height: 48, fontFamily: 'var(--font-medium)', fontSize: 15, color: 'var(--primary)',
              animation: 'reflectionCheckPop 0.4s ease-out',
            }}>
              <span style={{ fontSize: 20 }}>{'\u2713'}</span>
              {t('reflection_saved', lang)}
            </div>
          ) : (
            <>
              {saveError && (
                <div
                  role="alert"
                  style={{
                    marginBottom: 10,
                    padding: '10px 14px',
                    borderRadius: 10,
                    background: 'rgba(168, 0, 63, 0.08)',
                    border: '1px solid rgba(168, 0, 63, 0.18)',
                    color: 'var(--primary, #A8003F)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    lineHeight: 1.45,
                    textAlign: 'center',
                  }}
                >
                  {t('reflection_save_error', lang)}
                </div>
              )}
              <button
                type="button" className="reflection-save-btn"
                onClick={handleSave}
                disabled={saving || !hasData}
                style={{
                  width: '100%', height: 48, background: 'var(--primary)', color: '#fff',
                  border: 'none', borderRadius: 24,
                  fontFamily: 'var(--font-medium)', fontSize: 15,
                  cursor: (saving || !hasData) ? 'default' : 'pointer',
                  opacity: saving ? 0.6 : !hasData ? 0.4 : 1,
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'opacity 0.2s, transform 0.1s',
                }}
              >
                {saving
                  ? t('one_moment', lang)
                  : saveError
                    ? t('reflection_save_retry', lang)
                    : t('reflection_save', lang)}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Body Tab ──

function BodyTab({ bodyFeelings, toggleBody, lang }) {
  return (
    <>
      <div style={questionStyle}>
        {t('reflection_body_question', lang)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {BODY_OPTIONS.map((opt) => {
          const isSelected = bodyFeelings.has(opt);
          return (
            <button type="button" key={opt} className="reflection-card" onClick={() => toggleBody(opt)} style={cardStyle(isSelected)}>
              <ReflectionIcon name={opt} selected={isSelected} />
              <span style={cardTextStyle}>{t(`reflection_body_${opt}`, lang)}</span>
              {isSelected && <span style={checkMarkStyle}>{'\u2713'}</span>}
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Cycle Tab ──

// Soft teardrop glyph that scales with flow level. The "none" variant is
// rendered as an empty outline so the unselected state still has a focal
// shape, not a void. Stroke + fill pull from currentColor so the icon
// inherits the active/inactive tint set on the wrapper.
function FlowDrop({ level, selected }) {
  const sizes = { none: 14, spotting: 14, light: 16, medium: 19, heavy: 22 };
  const size = sizes[level] || 16;
  const filled = level !== 'none' && selected;
  // Selected drop uses --primary (deep rose) — see PeriodQuickSheet's
  // FlowDrop for the rationale. The previous --push token resolved
  // to slate-blue on this theme, which read as "wrong colour" against
  // a menstrual-flow context.
  const color = selected ? 'var(--primary, #A8003F)' : 'var(--warm-brown)';
  return (
    <svg
      width={size} height={size + 4} viewBox="0 0 24 28" fill="none"
      style={{ flexShrink: 0, color }}
      aria-hidden="true"
    >
      <path
        d="M12 2 C 12 2, 4 13, 4 19 a 8 8 0 0 0 16 0 C 20 13, 12 2, 12 2 Z"
        stroke="currentColor"
        strokeWidth={1.6}
        fill={filled ? 'currentColor' : 'none'}
      />
    </svg>
  );
}

// Inline glyph for floor-feel options. Rests on the same currentColor
// pattern as ReflectionIcon so the rose/warm-brown swap is automatic.
function FloorFeelIcon({ name, selected }) {
  const common = {
    width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
    style: { flexShrink: 0, color: selected ? 'var(--primary)' : 'var(--warm-brown)' },
  };
  const stroke = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'heavy':
      // downward weight — concentric arcs sinking
      return (
        <svg {...common}>
          <path d="M5 9h14M5 13h14M5 17h14" {...stroke} />
          <path d="M9 20l3 2 3-2" {...stroke} />
        </svg>
      );
    case 'aching':
      // pulse / heartbeat
      return (
        <svg {...common}>
          <path d="M3 12h4l2-5 4 10 2-5h6" {...stroke} />
        </svg>
      );
    case 'quiet':
      // closed flat line — silent
      return (
        <svg {...common}>
          <path d="M3 12h18" {...stroke} />
          <circle cx="12" cy="12" r="1.6" fill="currentColor" />
        </svg>
      );
    case 'strong':
      // upward chevrons — lift
      return (
        <svg {...common}>
          <path d="M5 18l7-7 7 7M5 12l7-7 7 7" {...stroke} />
        </svg>
      );
    case 'sensitive':
      // sparkle (matches body 'sensitive')
      return (
        <svg {...common}>
          <path d="M12 4v3M12 17v3M4 12h3M17 12h3M7 7l2 2M15 15l2 2M7 17l2-2M15 9l2-2" {...stroke} />
        </svg>
      );
    default:
      return (
        <svg {...common}><circle cx="12" cy="12" r="6" {...stroke} /></svg>
      );
  }
}

function CycleTab({ flow, setFlow, floorFeel, setFloorFeel, symptoms, setSymptoms, menopauseSymptoms, setMenopauseSymptoms, menopauseModeOn, note, setNote, lang }) {
  function toggleSymptom(s) {
    setSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }
  function toggleMenopauseSymptom(s) {
    setMenopauseSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }
  return (
    <>
      <div style={questionStyle}>
        {t('reflection_flow_question', lang)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {FLOW_OPTIONS.map((opt) => {
          const isSelected = flow === opt;
          return (
            <button
              type="button" key={opt}
              className="reflection-card"
              onClick={() => setFlow(isSelected ? null : opt)}
              style={cardStyle(isSelected)}
            >
              <FlowDrop level={opt} selected={isSelected} />
              <span style={cardTextStyle}>{t(`reflection_flow_${opt}`, lang)}</span>
              {isSelected && <span style={checkMarkStyle}>{'✓'}</span>}
            </button>
          );
        })}
      </div>

      {/* Floor-feel question: this is the bridge that makes period
          tracking inseparable from pelvic-floor awareness. We show it
          regardless of flow so users can also log floor sensations
          on non-bleed days, building a richer body-letter over time. */}
      <div style={questionStyle}>
        {t('reflection_floor_feel_question', lang)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {FLOOR_FEEL_OPTIONS.map((opt) => {
          const isSelected = floorFeel === opt;
          return (
            <button
              type="button" key={opt}
              className="reflection-card"
              onClick={() => setFloorFeel(isSelected ? null : opt)}
              style={cardStyle(isSelected)}
            >
              <FloorFeelIcon name={opt} selected={isSelected} />
              <span style={cardTextStyle}>{t(`reflection_floor_feel_${opt}`, lang)}</span>
              {isSelected && <span style={checkMarkStyle}>{'✓'}</span>}
            </button>
          );
        })}
      </div>

      {/* Symptoms: optional grid of small toggleable pills.
          Catches up to feature parity with mainstream period
          trackers (cramps, mood, energy, breast tenderness, etc.)
          while keeping the visual weight low so it doesn't compete
          with the primary flow / floor-feel decisions above. */}
      <div style={{ ...questionStyle, marginTop: 28 }}>
        {t('reflection_symptoms_question', lang)}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 8,
      }}>
        {SYMPTOM_OPTIONS.map((opt) => {
          const isSelected = symptoms.has(opt);
          return (
            <button
              type="button" key={opt}
              className="reflection-pill"
              onClick={() => toggleSymptom(opt)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                minHeight: 44,
                borderRadius: 12,
                background: isSelected ? 'var(--primary-pale)' : 'var(--surface)',
                border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                color: isSelected ? 'var(--primary)' : 'var(--dark-brown)',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                fontWeight: isSelected ? 600 : 500,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                textAlign: 'left',
                transition: 'background 180ms ease, border-color 180ms ease',
              }}
            >
              <span style={{
                width: 14, height: 14, borderRadius: 4,
                background: isSelected ? 'var(--primary)' : 'transparent',
                border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                flexShrink: 0,
              }} aria-hidden="true">
                {isSelected && '✓'}
              </span>
              <span>{t(`reflection_symptom_${opt}`, lang)}</span>
            </button>
          );
        })}
      </div>

      {/* Menopause-stage symptoms. Only rendered when the user has
          self-declared peri / meno / post via the menopause stage
          tile (isMenopauseModeActive). Subset of Greene Climacteric
          Scale items + GSM cluster (vaginal dryness, urinary
          urgency, painful sex) that PFMT directly impacts per the
          AUA/SUFU/AUGS 2025 GSM guideline. The intro line frames
          the question gently so the section reads as care, not
          surveillance. */}
      {menopauseModeOn && (
        <>
          <div style={{ ...questionStyle, marginTop: 28 }}>
            {t('reflection_menopause_symptoms_question', lang)}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 8,
          }}>
            {MENOPAUSE_SYMPTOM_OPTIONS.map((opt) => {
              const isSelected = menopauseSymptoms.has(opt);
              return (
                <button
                  type="button" key={opt}
                  className="reflection-pill"
                  onClick={() => toggleMenopauseSymptom(opt)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    minHeight: 44,
                    borderRadius: 12,
                    background: isSelected ? 'var(--primary-pale)' : 'var(--surface)',
                    border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                    color: isSelected ? 'var(--primary)' : 'var(--dark-brown)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: isSelected ? 600 : 500,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    textAlign: 'left',
                    transition: 'background 180ms ease, border-color 180ms ease',
                  }}
                >
                  <span style={{
                    width: 14, height: 14, borderRadius: 4,
                    background: isSelected ? 'var(--primary)' : 'transparent',
                    border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    flexShrink: 0,
                  }} aria-hidden="true">
                    {isSelected && '✓'}
                  </span>
                  <span>{t(`reflection_menopause_symptom_${opt}`, lang)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Free-text note. Optional. The italic placeholder leaves the
          UX feeling like an invitation rather than a form field. The
          textarea grows to ~5 lines so users can write a real
          sentence or two without being cramped. */}
      <div style={{ ...questionStyle, marginTop: 28 }}>
        {t('reflection_note_question', lang)}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t('reflection_note_placeholder', lang)}
        rows={5}
        maxLength={1200}
        style={{
          width: '100%',
          minHeight: 120,
          padding: '14px 16px',
          borderRadius: 14,
          border: '1.5px solid var(--border)',
          background: 'var(--surface)',
          fontFamily: 'var(--font-body)',
          fontSize: 16,
          lineHeight: 1.55,
          color: 'var(--dark-brown)',
          resize: 'vertical',
          outline: 'none',
          transition: 'border-color 180ms ease',
          WebkitAppearance: 'none',
          boxSizing: 'border-box',
        }}
        onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; }}
        onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; }}
      />
      <div style={{
        fontFamily: 'var(--font-ui)',
        fontSize: 11,
        color: 'var(--warm-brown)',
        marginTop: 6,
        textAlign: 'right',
      }}>
        {note.length}/1200
      </div>
    </>
  );
}

// ── Signals Tab ──

function SignalsTab({ signalSub, setSignalSub, sexualDesire, setSexualDesire, lubrication, setLubrication, bowel, setBowel, lang }) {
  return (
    <>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, justifyContent: 'center' }}>
        {SIGNAL_SUB_KEYS.map((key, idx) => {
          const isActive = signalSub === idx;
          return (
            <button
              type="button" key={key}
              className="reflection-pill"
              onClick={() => setSignalSub(idx)}
              style={{
                padding: '7px 14px', borderRadius: 999,
                background: isActive ? 'var(--primary)' : 'var(--surface)',
                color: isActive ? '#fff' : 'var(--primary)',
                border: `1px solid ${isActive ? 'var(--primary)' : 'color-mix(in srgb, var(--primary) 28%, transparent)'}`,
                fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                boxShadow: isActive ? '0 6px 18px -10px rgba(168,0,63,0.38)' : 'none',
                transition: 'background 200ms ease, color 200ms ease, box-shadow 200ms ease',
              }}
            >
              {t(`reflection_signals_${key}`, lang)}
            </button>
          );
        })}
      </div>

      <div style={{ animation: 'reflectionFadeIn 0.2s ease-out' }} key={signalSub}>
        {signalSub === 0 && (
          <>
            <div style={questionStyle}>{t('reflection_desire_question', lang)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {DESIRE_OPTIONS.map((opt) => (
                <button type="button" key={opt} className="reflection-card" onClick={() => setSexualDesire(opt)} style={cardStyle(sexualDesire === opt)}>
                  <span style={cardTextStyle}>{t(`reflection_desire_${opt}`, lang)}</span>
                  {sexualDesire === opt && <span style={checkMarkStyle}>{'\u2713'}</span>}
                </button>
              ))}
            </div>
          </>
        )}

        {signalSub === 1 && (
          <>
            <div style={questionStyle}>{t('reflection_lubrication_question', lang)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {LUBRICATION_OPTIONS.map((opt) => (
                <button type="button" key={opt} className="reflection-card" onClick={() => setLubrication(opt)} style={cardStyle(lubrication === opt)}>
                  <span style={cardTextStyle}>{t(`reflection_lubrication_${opt}`, lang)}</span>
                  {lubrication === opt && <span style={checkMarkStyle}>{'\u2713'}</span>}
                </button>
              ))}
            </div>
          </>
        )}

        {signalSub === 2 && (
          <>
            <div style={questionStyle}>{t('reflection_bowel_question', lang)}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {BOWEL_OPTIONS.map((opt) => (
                <button type="button" key={opt} className="reflection-card" onClick={() => setBowel(opt)} style={cardStyle(bowel === opt)}>
                  <span style={cardTextStyle}>{t(`reflection_bowel_${opt}`, lang)}</span>
                  {bowel === opt && <span style={checkMarkStyle}>{'\u2713'}</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Intimate Tab ──

function IntimateTab({ hadActivity, setHadActivity, experience, setExperience, lang }) {
  return (
    <>
      <div style={questionStyle}>{t('reflection_intimate_question', lang)}</div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {['yes', 'no'].map((val) => {
          const isSelected = hadActivity === val;
          return (
            <button
              type="button" key={val}
              className="reflection-card"
              onClick={() => { setHadActivity(val); if (val === 'no') setExperience(null); }}
              style={{
                flex: 1, padding: '20px 16px',
                background: isSelected ? 'var(--surface-alt)' : '#fff',
                border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 12, cursor: 'pointer',
                textAlign: 'center',
                WebkitTapHighlightColor: 'transparent',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <ReflectionIcon name={val} selected={isSelected} />
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: 17,
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? 'var(--primary)' : 'var(--dark-brown)',
              }}>
                {t(`reflection_intimate_${val}`, lang)}
              </div>
            </button>
          );
        })}
      </div>

      {hadActivity === 'yes' && (
        <div style={{ animation: 'reflectionFadeIn 0.25s ease-out' }}>
          <div style={questionStyle}>{t('reflection_experience_question', lang)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {EXPERIENCE_OPTIONS.map((opt) => (
              <button type="button" key={opt} className="reflection-card" onClick={() => setExperience(opt)} style={cardStyle(experience === opt)}>
                <span style={cardTextStyle}>{t(`reflection_experience_${opt}`, lang)}</span>
                {experience === opt && <span style={checkMarkStyle}>{'\u2713'}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Shared styles ──


function cardStyle(isSelected) {
  return {
    display: 'flex', alignItems: 'center', gap: 12,
    width: '100%', textAlign: 'left',
    padding: '16px 18px',
    minHeight: 56,
    background: isSelected ? 'var(--primary-pale)' : 'var(--surface)',
    border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
    borderRadius: 14,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: isSelected
      ? '0 8px 22px -14px rgba(168, 0, 63, 0.32)'
      : '0 1px 6px rgba(45, 26, 31, 0.03)',
    transition:
      'background 180ms ease, border-color 180ms ease, transform 120ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 220ms ease',
  };
}


